import moment from 'moment';
import { AppConfig } from '@app/app.config';
import { AuthenticationService } from '@app/security/shared/authentication.service';
import { GuidService } from '@app/shared/services/guid.service';
import { HttpOptionsService, HttpService } from '@app/security/shared/http.service';
import { Injectable } from '@angular/core';
import { Logger } from '@app/logger/logger';
import { LocalStorageRefService } from '@app/shared/services/browser/local-storage-ref.service';
import { StringResult } from '@app/shared/models/string-result.model';
import { from, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

const lgSfx = '\t(seat-tracker.service)';

// SEAT_TOKEN_PING_INTERVAL is expected in Seconds
const SEAT_TOKEN_PING_INTERVAL = 60;
const SEAT_TOKEN_LOCK_NAME = 'aujs-seat-token-keepalive';
const SEAT_TOKEN_LOCALSTORAGE_LOCK_KEY = 'aujs-seat-token-keepalive-lock-localstorage';
const SEAT_TOKEN_LOCK_TIMEOUT_MS = 15_000;
const SEAT_TOKEN_LOCK_RETRY_MS = 100;
const ACQUIRE_SEAT_HEADER = 'acquire_seat';

interface SeatTokenLocalStorageLock {
  lockId: string;
  expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class SeatTrackerService {
  private _seatTokenRetrieved = new Subject<void>();
  private seatRecoveryPromise: Promise<boolean> | undefined;

  public SeatTokenRetrievedObs = this._seatTokenRetrieved.asObservable();

  public get seatToken(): string {
    return this.localStorageService.seatToken;
  }

  public get seatTokenTimeStamp(): string {
    return this.localStorageService.seatTokenTimeStamp;
  }

  constructor(
    private authService: AuthenticationService,
    private config: AppConfig,
    private guidService: GuidService,
    private http: HttpService,
    private httpOptionsService: HttpOptionsService,
    private localStorageService: LocalStorageRefService,
    private logger: Logger
  ) {
    this.authService.retryAuth.subscribe(() => {
      this.runKeepAlive(true, true);
    });
  }

  public checkSeatToken(): void {
    try {
      if (this.shouldRefreshSeatToken()) {
        this.runKeepAlive();
      }
    } finally {
      setTimeout(() => {
        this.checkSeatToken();
      }, SEAT_TOKEN_PING_INTERVAL * 1000);
    }
  }

  public runKeepAlive(sendRetryLoginMsg?: boolean, force = false): void {
    const startingSeatToken = this.localStorageService.seatToken;

    this.withSeatTokenLock(() =>
      this.runKeepAliveLocked(sendRetryLoginMsg, force, startingSeatToken)
    ).catch((error) => this.logger.error('Seat token keep alive lock failed:', error));
  }

  public recoverSeat(): Observable<boolean> {
    if (!this.seatRecoveryPromise) {
      // every 401 in this tab waits for the same recovery, and the lock coordinates recovery across tabs.
      this.seatRecoveryPromise = this.startSeatRecovery().finally(() => {
        this.seatRecoveryPromise = undefined;
      });
    }

    return from(this.seatRecoveryPromise);
  }

  public KeepDbSessionAlive(): void {
    // Note: this interval timing may need to be adjusted
    setInterval(() => this.keepAliveDb(), 45000);
  }

  private keepAliveDb(): void {
    this.logger.log('Keep alive db interval');
    if (this.authService.token) {
      this.logger.log('Calling keep db session alive', moment().format('LTS'));

      const API_ENDPOINT = `/api/v1/auth/keepalive-db/`;
      const endpoint = this.config.getServerUri() + API_ENDPOINT;

      this.http.postAny(endpoint, undefined, undefined, true).subscribe((resp) => {
        this.logger.log('Keep alive db response', resp);
      });
    }
  }

  private keepAlive(allowSeatAcquisition = false): Observable<StringResult> {
    const seatToken = this.localStorageService.seatToken;
    this.logger.log('Sending keep alive request with token', seatToken, lgSfx);

    const API_ENDPOINT = `/api/v1/auth/keepalive/`;
    const endpoint = this.config.getServerUri() + API_ENDPOINT;
    const options = this.httpOptionsService.get();

    if (allowSeatAcquisition) {
      // Only a failed login retry explicitly asks the anonymous endpoint to allocate a new seat.
      options.headers = options.headers.set(ACQUIRE_SEAT_HEADER, 'true');
    }

    return this.http
      .postNoLoading(endpoint, undefined, StringResult, options, true)
      .pipe(map((resp) => resp));
  }

  private async runKeepAliveLocked(
    sendRetryLoginMsg?: boolean,
    force = false,
    startingSeatToken = ''
  ): Promise<void> {
    if (!force && !this.shouldRefreshSeatToken()) {
      return;
    }

    if (
      force &&
      startingSeatToken &&
      this.localStorageService.seatToken &&
      this.localStorageService.seatToken !== startingSeatToken
    ) {
      // if this hits another tab already swapped the seat token
      this.notifyRetryTokenRetrieved(sendRetryLoginMsg);
      return;
    }

    await this.requestSeatToken(sendRetryLoginMsg === true);
  }

  private async startSeatRecovery(): Promise<boolean> {
    const startingSeatToken = this.localStorageService.seatToken;

    const recovered = await this.withSeatTokenLock(() => {
      // A preceding tab already recovered if it replaced the token while this tab waited for the lock.
      if (startingSeatToken && this.localStorageService.seatToken !== startingSeatToken) {
        return Promise.resolve(true);
      }

      return this.requestSeatToken();
    });

    return recovered === true;
  }

  private async requestSeatToken(allowSeatAcquisition = false): Promise<boolean> {
    try {
      const result = await this.keepAlive(allowSeatAcquisition).toPromise();
      this.logger.log(
        'Keep alive request returned with token',
        result,
        moment().format('LTS'),
        lgSfx
      );

      if (!result?.result) {
        this.logger.error(`Keep alive returned invalid token:`, result);
        return false;
      }

      this.localStorageService.seatToken = result.result;
      this.localStorageService.seatTokenTimeStamp = moment().format();
      this.notifyRetryTokenRetrieved(allowSeatAcquisition);
      return true;
    } catch (error) {
      this.logger.error(`Keep alive failed:`, error.message, error.description);
      return false;
    }
  }

  private shouldRefreshSeatToken(): boolean {
    const currentTimeStamp = this.seatTokenTimeStamp;

    return (
      !currentTimeStamp ||
      !this.seatToken ||
      moment.duration(moment().diff(moment(currentTimeStamp))).asSeconds() >
        SEAT_TOKEN_PING_INTERVAL
    );
  }

  private notifyRetryTokenRetrieved(sendRetryLoginMsg?: boolean): void {
    if (sendRetryLoginMsg) {
      this._seatTokenRetrieved.next();
    }
  }

  private withSeatTokenLock<T>(callback: () => Promise<T>): Promise<T | undefined> {
    if (navigator.locks?.request) {
      return navigator.locks.request(SEAT_TOKEN_LOCK_NAME, callback).catch((error) => {
        this.logger.error('Seat token lock failed:', error);
        return undefined;
      });
    }

    return this.withLocalStorageSeatTokenLock(callback);
  }

  private async withLocalStorageSeatTokenLock<T>(
    callback: () => Promise<T>
  ): Promise<T | undefined> {
    const waitUntil = Date.now() + SEAT_TOKEN_LOCK_TIMEOUT_MS;
    let lockId = this.tryAcquireLocalStorageLock();

    // wait for the other tab so its successful recovery can satisfy every queued request.
    while (!lockId && Date.now() < waitUntil) {
      await new Promise((resolve) => setTimeout(resolve, SEAT_TOKEN_LOCK_RETRY_MS));
      lockId = this.tryAcquireLocalStorageLock();
    }

    if (!lockId) {
      return undefined;
    }

    try {
      return await callback();
    } finally {
      this.releaseLocalStorageLock(lockId);
    }
  }

  // alternative to browser's lock api which is unavailable in non-secure (http) contexts
  private tryAcquireLocalStorageLock(): string | null {
    const lockId = this.guidService.newGuid();
    const now = Date.now();

    const localLock = this.getLocalStorageLock();

    // existing lock that hasn't expired or been released yet
    if (localLock && localLock.expiresAt > now) {
      return null;
    }

    localStorage.setItem(
      SEAT_TOKEN_LOCALSTORAGE_LOCK_KEY,
      JSON.stringify({
        lockId,
        expiresAt: now + SEAT_TOKEN_LOCK_TIMEOUT_MS
      })
    );

    // repull lock to make sure another tab didn't overwrite
    const confirmedLock = this.getLocalStorageLock();

    return confirmedLock?.lockId === lockId ? lockId : null;
  }

  private releaseLocalStorageLock(lockId: string): void {
    const lock = this.getLocalStorageLock();

    // if the current lock in storage matches what we are trying to release then delete it
    if (lock?.lockId === lockId) {
      localStorage.removeItem(SEAT_TOKEN_LOCALSTORAGE_LOCK_KEY);
    }
  }

  private getLocalStorageLock(): SeatTokenLocalStorageLock | null {
    const localLock = localStorage.getItem(SEAT_TOKEN_LOCALSTORAGE_LOCK_KEY);

    if (!localLock) {
      return null;
    }

    try {
      const lock = JSON.parse(localLock);

      if (lock && typeof lock.lockId === 'string' && typeof lock.expiresAt === 'number') {
        return lock;
      }
    } catch (error) {
      this.logger.warn('Invalid seat token lock found in localStorage:', error);
    }

    localStorage.removeItem(SEAT_TOKEN_LOCALSTORAGE_LOCK_KEY);
    return null;
  }
}
